import { useEffect, useState, useRef } from 'react';
import { Loader2 } from 'lucide-react';
import Dialog from '../Dialog/Dialog';

const FlywirePayment = ({
  sessionId,
  frontendKey = process.env.REACT_APP_FLYWIRE_FRONTEND_KEY,
  onSuccess,
  bookingData
}) => {
  const [loading, setLoading] = useState(true);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [invoiceId, setInvoiceId] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [sdkLoaded, setSdkLoaded] = useState(false);
  const elementRef = useRef(null);
  const containerRef = useRef(null);

  // Load Flywire SDK script
  useEffect(() => {
    console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
    console.log("💳 [FLYWIRE SDK] LOADING SDK...");
    console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
    console.log("  🔑 Frontend Key:", frontendKey ? frontendKey.substring(0, 10) + '...' : 'MISSING');
    console.log("  🌍 Environment:", process.env.REACT_APP_ENV || 'development');

    // Check if frontend key is available
    if (!frontendKey) {
      console.error("❌ CRITICAL: Frontend key is missing!");
      setErrorMessage('Configuration error: Flywire frontend key is missing. Please check your environment variables.');
      setShowErrorDialog(true);
      setLoading(false);
      return;
    }

    // Check if script is already loaded
    if (window.FlywireSDK) {
      console.log("✅ SDK already loaded");
      setSdkLoaded(true);
      return;
    }

    // Create and load the SDK script
    const script = document.createElement('script');
    const sdkUrl = 'https://artifacts.flywire.com/sdk/js/v0/main.js';

    script.src = sdkUrl;
    script.async = true;
    script.crossOrigin = 'anonymous';

    console.log("  📥 Loading SDK from:", sdkUrl);

    script.onload = () => {
      console.log("✅ Flywire SDK loaded successfully");
      setSdkLoaded(true);
    };

    script.onerror = () => {
      console.error("❌ Failed to load Flywire SDK");
      setErrorMessage('Failed to load payment form. Please refresh the page.');
      setShowErrorDialog(true);
      setLoading(false);
    };

    document.head.appendChild(script);

    return () => {
      // Cleanup: remove script if component unmounts
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [frontendKey]);

  // Initialize Flywire SDK and create payment element
  useEffect(() => {
    if (!sdkLoaded || !sessionId) {
      if (!sessionId) {
        console.log("\n❌ CRITICAL: Session ID is missing!");
        setErrorMessage('Session ID is missing. Please try again.');
        setShowErrorDialog(true);
        setLoading(false);
      }
      return;
    }

    console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
    console.log("💳 [FLYWIRE SDK] INITIALIZING PAYMENT ELEMENT");
    console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
    console.log("⏰ Timestamp:", new Date().toISOString());
    console.log("  🆔 Session ID:", sessionId);

    let mounted = true;

    (async () => {
      try {
        // Initialize SDK
        console.log("\n🚀 Initializing Flywire SDK...");
        const sdk = await window.FlywireSDK(frontendKey);
        console.log("✅ SDK initialized");

        // Create elements instance with styling
        console.log("\n🎨 Creating elements instance...");
        const elements = await sdk.elements({
          appearance: {
            fonts: [
              {
                url: "https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700&display=swap",
                fontFamily: "Roboto",
              },
            ],
            variables: {
              primaryColor: "#0066cc",
              fontFamily: "Roboto, sans-serif"
            },
          },
          locale: "en",
        });
        console.log("✅ Elements created");

        // Create payment element
        console.log("\n💳 Creating payment element...");
        const element = await elements.create("payment", {
          sessionId: sessionId,
          displayMode: "container",
          fields: {
            first_name: { hidden: false },
            last_name: { hidden: false },
            country: { hidden: true },
            address: { hidden: true },
            city: { hidden: true },
            phone: { hidden: false },
            email: { hidden: false },
            state: { hidden: false },
            zip: { hidden: false },
          },
        });

        if (!mounted) {
          console.log("⚠️  Component unmounted, aborting...");
          return;
        }

        elementRef.current = element;
        console.log("✅ Payment element created");

        // Handle success event
        element.onEvent("success", async (data) => {
          console.log('\n✅ ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
          console.log('✅ [FLYWIRE SDK] PAYMENT SUCCESSFUL!');
          console.log('✅ ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
          console.log('  📦 Payment Data:', data);

          setLoading(false);

          try {
            console.log('\n🚀 SENDING SUCCESS DATA TO BACKEND...');
            console.log('  🔗 Endpoint: https://api.ctsdmcwpc.com/api/flywire-success');

            const response = await fetch('https://api.ctsdmcwpc.com/api/flywire-success', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                ...data,
                bookingData
              }),
            });

            console.log('\n📡 BACKEND RESPONSE:');
            console.log('  📊 HTTP Status:', response.status, response.statusText);

            if (response.ok) {
              const backendResponse = await response.json();
              console.log('  ✅ Response Data:', backendResponse);

              const zohoInvoiceId = backendResponse.zohoOrder?.invoice_id ||
                backendResponse.invoice_id ||
                backendResponse.invoiceId ||
                'N/A';

              console.log('\n✅ ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
              console.log('✅ PAYMENT PROCESSING COMPLETE!');
              console.log('✅ ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
              console.log('  🎫 Invoice:', zohoInvoiceId);
              console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n');

              setInvoiceId(zohoInvoiceId);
              setShowSuccessDialog(true);

              if (onSuccess) {
                onSuccess(data);
              }
            } else {
              throw new Error(`Backend confirmation failed: ${response.status}`);
            }
          } catch (error) {
            console.log('\n❌ ERROR PROCESSING PAYMENT SUCCESS');
            console.log('  🔴 Error:', error.message);
            setErrorMessage('Failed to process your order. Please contact support.');
            setShowErrorDialog(true);
          }
        });

        // Handle error event
        element.onEvent("error", async (errorData) => {
          console.log('\n❌ ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
          console.log('❌ [FLYWIRE SDK] PAYMENT ERROR!');
          console.log('❌ ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
          console.log('  📦 Error Data:', errorData);

          const errorMsg = errorData.error?.message ||
            errorData.message ||
            'Payment failed. Please try again.';

          console.log('  💬 Error Message:', errorMsg);
          console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n');

          setErrorMessage(errorMsg);
          setShowErrorDialog(true);
          setLoading(false);

          // Send error to backend
          try {
            await fetch('https://api.ctsdmcwpc.com/api/flywire-error', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(errorData),
            });
            console.log('  ✅ Error logged to backend\n');
          } catch (backendError) {
            console.log('  ❌ Failed to log error to backend:', backendError.message);
          }
        });

        // Mount the element
        console.log("\n🎯 Mounting payment element to container...");
        element.mount("flywire-payment-container");
        console.log("✅ Payment element mounted successfully");
        console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n");

        setLoading(false);

      } catch (error) {
        console.log('\n❌ ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        console.log('❌ SDK INITIALIZATION FAILED!');
        console.log('❌ ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        console.log('  🔴 Error:', error.message);
        console.log('  📚 Stack:', error.stack);
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n');

        if (mounted) {
          setErrorMessage('Failed to initialize payment form. Please try again.');
          setShowErrorDialog(true);
          setLoading(false);
        }
      }
    })();

    return () => {
      mounted = false;
      // Cleanup: unmount element if it exists
      if (elementRef.current) {
        try {
          // Flywire SDK elements typically have an unmount method
          console.log("🧹 Cleaning up Flywire payment element");
        } catch (error) {
          console.log("⚠️  Error cleaning up element:", error);
        }
      }
    };
  }, [sdkLoaded, sessionId, frontendKey, onSuccess]);


  const handleSuccessDialogClose = () => {
    setShowSuccessDialog(false);
    // Redirect after closing
    setTimeout(() => {
      window.location.href = '/booking-confirmation';
    }, 500);
  };

  const handleErrorDialogClose = () => {
    setShowErrorDialog(false);
    setErrorMessage('');
  };

  if (!sessionId) {
    return (
      <div className="flywire-payment-container">
        <div className="flywire-error">
          <p className="text-red-600">Session ID is missing. Please try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flywire-payment-container">
      {loading && (
        <div className="flywire-loading">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          <p className="mt-2 text-gray-600">Loading payment form...</p>
        </div>
      )}

      {/* Flywire SDK Payment Container */}
      <div
        id="flywire-payment-container"
        className="flywire-payment-element"
        ref={containerRef}
        style={{
          display: loading ? 'none' : 'block'
        }}
      />

      {/* Success Dialog */}
      <Dialog
        isOpen={showSuccessDialog}
        onClose={handleSuccessDialogClose}
        type="success"
        title="Order Placed Successfully!"
        message="Your payment has been processed and your order has been placed."
        invoiceId={invoiceId}
      />

      {/* Error Dialog */}
      <Dialog
        isOpen={showErrorDialog}
        onClose={handleErrorDialogClose}
        type="error"
        title="Order Not Placed"
        message={errorMessage}
        showRetryButton={false}
      />
    </div>
  );
};

export default FlywirePayment;